import type { ManagerPattern } from '../types/patterns';

/**
 * The command-center "Now" dataset - the recurring playbook violations a manager
 * is asked to handle, most urgent first. Each pattern groups concrete cases that
 * mirror the onboarding breaks gallery, so the demo tells one continuous story:
 * the gallery reveals what Intera caught, the command center turns it into the
 * few decisions that matter today.
 */
export const MANAGER_PATTERNS: ManagerPattern[] = [
  {
    id: 'repeat-data-access-requests',
    severity: 'high',
    isRecurring: true,
    title: 'The same data-access request keeps coming back by hand',
    playbookRule:
      'A request that recurs across engineers should be granted by a self-service flow, not a fresh ticket and a manual approval each time.',
    pagerLabel: 'Repeat access asks',
    metrics: [
      { value: '6×', label: 'same request this month', isUrgent: true },
      {
        value: '+3',
        unit: 'days',
        label: 'avg wait per grant',
        isUrgent: true,
      },
      { value: '5', label: 'engineers blocked' },
      { value: '~9h', label: 'your time spent granting' },
    ],
    cases: [
      {
        id: 'access-318',
        severity: 'high',
        ref: 'ACCESS-318',
        ageLabel: '1h ago',
        title: 'Read access to the analytics warehouse requested - again',
        quote: 'Need read access to the revenue schema to debug a report',
        attribution: 'Diego Ramos · this morning',
        platforms: ['slack', 'jira'],
        owner: { name: 'Diego Ramos', initials: 'DR' },
        impactTag: '4th identical ask',
        actionLabel: 'Grant access',
      },
      {
        id: 'access-301',
        severity: 'medium',
        ref: 'ACCESS-301',
        ageLabel: 'yesterday',
        title: 'Same warehouse read access, different engineer and team',
        quote: 'Can someone grant me read on the analytics DB?',
        attribution: 'Priya Shah (Growth) · yesterday',
        platforms: ['slack'],
        owner: { name: 'Priya Shah', initials: 'PS' },
        impactTag: 'waited 3 days last time',
        actionLabel: 'Grant access',
      },
      {
        id: 'access-289',
        severity: 'low',
        ref: 'ACCESS-289',
        ageLabel: '4 days ago',
        title:
          'Reporting read access expired, re-requested through the ticket queue',
        quote: 'My access lapsed - reopening the same request',
        attribution: 'Amelia Ng · last Tuesday',
        platforms: ['bmc-helix', 'jira'],
        owner: { name: 'Amelia Ng', initials: 'AN' },
        impactTag: '2nd time this quarter',
        actionLabel: 'Re-grant access',
      },
    ],
    recommendation: {
      eyebrow: 'Intera suggests',
      title: 'Stand up a self-service data-access portal',
      rationale:
        'This exact request has landed 6 times this month across 3 teams, each waiting ~3 days on a manual grant. A scoped self-service flow - auto-expiring, audited, read-only by default - would absorb it and stop it from reaching your queue again.',
      ctaLabel: 'Draft the self-service spec',
      proofPoints: [
        '6 identical requests · 3 teams',
        '~9h of your time back each month',
        'Auto-expiring, audited grants',
      ],
    },
  },
  {
    id: 'estimates-no-downstream-notice',
    severity: 'high',
    isRecurring: true,
    title: 'Estimates changed without looping in the downstream team',
    playbookRule:
      'Any re-estimate on a shared pipeline must notify dependent teams the same day.',
    pagerLabel: 'Estimates',
    metrics: [
      { value: '3×', label: 'violated this week', isUrgent: true },
      { value: '+11', unit: 'days', label: 'avg delay caused', isUrgent: true },
      { value: '4', label: 'dependents stranded' },
      { value: '2', label: 'bugs traced back' },
    ],
    cases: [
      {
        id: 'data-842',
        breakId: 'data-pipeline-reestimated',
        severity: 'high',
        ref: 'DATA-842',
        ageLabel: '2h ago',
        title: 'Ingestion ticket re-estimated 3×, reporting team never told',
        quote: 'Pushing delivery to Sprint 44',
        attribution: 'Sarah Chen · yesterday',
        platforms: ['jira', 'slack'],
        owner: { name: 'Sarah Chen', initials: 'SC' },
        impactTag: '+10 days · 3 dependents',
        actionLabel: 'Reschedule & notify',
      },
      {
        id: 'mob-410',
        breakId: 'spike-undersized',
        severity: 'high',
        ref: 'MOB-410',
        ageLabel: '2h ago',
        title: 'Pointed at 3, but the last 3 tickets like it shipped at ~12',
        quote: 'Estimate: 3 points',
        attribution: 'planned this sprint · Diego Ramos',
        platforms: ['jira'],
        owner: { name: 'Diego Ramos', initials: 'DR' },
        impactTag: 'flagged pre-sprint',
        actionLabel: 'Re-point & split',
      },
      {
        id: 'srch-771',
        breakId: 'engineer-overloaded',
        severity: 'high',
        ref: 'SRCH-771',
        ageLabel: '3h ago',
        title: 'One engineer owns 4 critical-path tickets this sprint',
        quote: '18 points in a 10-point capacity',
        attribution: 'current sprint',
        platforms: ['jira'],
        owner: { name: 'Amelia Ng', initials: 'AN' },
        impactTag: '+7 days · 4 tickets',
        actionLabel: 'Rebalance',
      },
    ],
  },
  {
    id: 'migrations-no-rollback',
    severity: 'medium',
    isRecurring: true,
    title: 'Migrations shipped without a rollback plan',
    playbookRule:
      'Every database migration must include a documented rollback plan before it can be reviewed.',
    pagerLabel: 'Missing rollback',
    metrics: [
      { value: '3×', label: 'this quarter', isUrgent: true },
      { value: '2', label: 'incidents caused' },
      { value: '1', label: 'P1 traced back' },
    ],
    cases: [
      {
        id: 'pr-4821',
        breakId: 'migration-no-rollback',
        severity: 'medium',
        ref: 'PR #4821',
        ageLabel: '5h ago',
        title: 'Schema migration opened with no rollback section',
        quote: 'No "Rollback" section present',
        attribution: 'opened by jdoe · 4h ago',
        platforms: ['github'],
        owner: { name: 'Jordan Doe', initials: 'JD' },
        impactTag: '3rd miss this quarter',
        actionLabel: 'Request changes',
      },
      {
        id: 'orders-api-331',
        breakId: 'api-contract-drift',
        severity: 'medium',
        ref: 'PR #5102',
        ageLabel: '8h ago',
        title: 'Endpoint drifted from the documented API contract',
        quote: 'nested customer { id, name }',
        attribution: 'merged · 8h ago',
        platforms: ['github', 'confluence'],
        owner: { name: 'Priya Shah', initials: 'PS' },
        impactTag: '2nd drift this month',
        actionLabel: 'Update contract',
      },
    ],
  },
  {
    id: 'team-not-looped-in',
    severity: 'high',
    isRecurring: true,
    title:
      'Decisions changed without looping in the team building against them',
    playbookRule:
      'When a launch date or architecture decision changes, the affected team must be pulled in the same day.',
    pagerLabel: 'Team not looped in',
    metrics: [
      { value: '2×', label: 'this week', isUrgent: true },
      {
        value: '+9',
        unit: 'days',
        label: 'wasted on stale work',
        isUrgent: true,
      },
      { value: '2', label: 'teams off-plan' },
    ],
    cases: [
      {
        id: 'gtm-shift',
        breakId: 'gtm-shift-sprint-unchanged',
        severity: 'high',
        ref: 'GROW-203',
        ageLabel: '6h ago',
        title: 'GTM delayed the promo 2 weeks - the eng sprint never changed',
        quote: 'move the promo to the 28th',
        attribution: 'Lena (GTM) · 6h ago',
        platforms: ['slack', 'jira'],
        owner: { name: 'Lena Ortiz', initials: 'LO' },
        impactTag: '+10 days · 2 tickets',
        actionLabel: 'Re-prioritize',
      },
      {
        id: 'adr-31',
        breakId: 'arch-decision-not-propagated',
        severity: 'medium',
        ref: 'RISK-664',
        ageLabel: '1d ago',
        title: 'An approved ADR never reached the team building against it',
        quote: 'against the SQS queue client',
        attribution: 'no reference to ADR-31',
        platforms: ['confluence', 'jira'],
        owner: { name: 'Tom Baker', initials: 'TB' },
        impactTag: '+8 days rework',
        actionLabel: 'Update ticket',
      },
    ],
  },
  {
    id: 'stale-dependency',
    severity: 'high',
    isRecurring: false,
    title: 'A reopened dependency left its dependents unverified',
    playbookRule:
      'When a reopened ticket has dependents, re-block them until the dependency is re-verified.',
    pagerLabel: 'Stale dependency',
    metrics: [
      { value: '1', label: 'P1 incident', isUrgent: true },
      { value: '3', label: 'dependents still ready' },
      {
        value: '+5',
        unit: 'days',
        label: 'exposure if shipped',
        isUrgent: true,
      },
    ],
    cases: [
      {
        id: 'pay-9281',
        breakId: 'stale-dependency-incident',
        severity: 'high',
        ref: 'PAY-9281',
        ageLabel: '3h ago',
        title:
          'A "done" dependency reopened after a P1, dependents not reverified',
        quote: 'duplicate charges on retried webhooks',
        attribution: 'INC-2041 · resolved 9h',
        platforms: ['pagerduty', 'jira'],
        owner: { name: 'Maya Cohen', initials: 'MC' },
        impactTag: '3 tickets building on it',
        actionLabel: 'Re-block dependents',
      },
    ],
  },
];
